import type { ListItem } from '@/types/Productlist';
import type { Address, FreightDeliveryOrderLinePatch, OrderLine, OrderText } from '@/types/Checkout';
import type { FreightDeliveryMethod } from '@/data/api/freight/freight.d';

import { LineKey, matchOrderRow } from '@/helpers/freight';
import { articleBlocked1299, convertUnit } from '@/data/api/products/helpers';

import dayjs, { _dayjs } from '@/helpers/dayjs';
import i18n from '@/languages/i18n';
import _ from 'lodash';
import ArticleHelper from './article';
import { Quote } from '@/types/Quotes';
import { User } from '@/stores/account.d';
import { getAccountContacts, saveQuote } from '@/data/api/kundportal/requests';
import { Article } from '@/types/Article';
import { PriceManager } from './pricemanager';
import { Packaging } from '@/data/api/packaging/packaging';
import { getArticleTitle } from '@/helpers';
import { recursiveGet, getArticleBaseUnitAmount } from '@/data/api/products/helpers';
import { PriceType } from '@/helpers/pricemanager';
import { useAccountStore } from '@/stores/account';

export function rowsToOrderlines(
  rows: ListItem[] = [],
  freightDelivery?: FreightDeliveryMethod,
  patch?: Record<string, FreightDeliveryOrderLinePatch>
): OrderLine[] {
  let orderlines = [] as OrderLine[];
  if (freightDelivery) {
    const deliveries = freightDelivery?.warehouses.flatMap((warehouse) => warehouse.delivery);
    orderlines = deliveries.reduce((acc, delivery) => {
      const deliveryOrderLines = delivery.orderLines.reduce((acc, orderLine) => {
        const lineKey = new LineKey(orderLine);
        let line = {
          itemNumber: orderLine.itemNumber,
          lineNumber: orderLine.lineNumber,
          lineNumberPosition: orderLine.lineNumberPosition,
          salesUnit: orderLine.unit,
          salesUnitQuantity: orderLine.quantity,
          jointDeliveryCode: delivery.jointDeliveryCode,
          transactionReason: orderLine.transactionReason,
        } as OrderLine;

        const existingRow = matchOrderRow(rows, orderLine);
        if (!existingRow) {
          console.debug('No matching row found for orderline', lineKey.toString());
          return acc;
        }

        // const existingRow = rows.find((row) => row.lineNumber === orderLine.lineNumber &&
        if (existingRow?.comment?.length) {
          line.preTexts = existingRow.comment.split('\n').map((line, index) => ({
            lineNumber: index + 1,
            text: line,
            typeCode: existingRow?.preDocumentClass ? existingRow.preDocumentClass : 'CO02',
          }));
        }

        if (existingRow?.batchnr && !existingRow._meta?.treatment) {
          line.batchNumber = existingRow.batchnr;
        }

        // Patch the row, if available
        const p = patch?.[lineKey.toString()];

        if (p && p.attributes.voided) {
          console.debug('Orderline is voided', lineKey.toString());
          return acc;
        }

        // BEN-1885: Handle TL/TO articles with 1000kg price quantity
        const article = existingRow.article;
        const isTLTOArticle =
          article?.alternativeUnits.some((unit) => unit.alternateType === 2 && unit.alternate === 't') &&
          article.userDefinedField1Item === 'FRAKT' &&
          ['TL', 'TO'].includes(article?.discountGroupItem) &&
          article?.basicUnitOfMeasure === 'kg';

        if (p && isTLTOArticle) {
          // If the patch is valid, e.g it's been changed, then we need to set the price quantity to 1000kg
          if (p.conditionalAttributes.isValid && p.conditionalAttributes.price) {
            p.conditionalAttributes.priceQuantity = 1000;
            p.attributes.priceUnit = 'kg';
          } else {
            p.conditionalAttributes.priceQuantity = undefined;
            p.attributes.priceUnit = 'kg';
          }
        }

        // Always applied patches
        if (p) {
          line.salesPriceUnitOfMeasure = p.attributes.priceUnit;
        }

        // Conditional patches
        if (p && p.conditionalAttributes.isValid) {
          for (const discount of p.conditionalAttributes.discounts ?? []) {
            line[`discountPercent${discount.index}`] = '' + discount.discount;
            line[`discountStatus${discount.index}`] = discount.status;
          }

          if (p.conditionalAttributes.price) {
            let price = p.conditionalAttributes.price.value;
            // convert 0 prices to 0.000001 to avoid 0 prices being ignored
            if (price === 0) price = 0.000001;

            line.salesPrice = '' + price;
          }

          if ((p.conditionalAttributes.priceQuantity ?? null) !== null) {
            line.salesPriceQuantity = '' + p.conditionalAttributes.priceQuantity;
          }
          line.actionReason = p.conditionalAttributes.actionReason;
        }

        acc.push(line);
        return acc;
      }, [] as OrderLine[]);
      return acc.concat(deliveryOrderLines.filter(Boolean));
    }, [] as OrderLine[]);
  } else {
    orderlines =
      rows?.reduce((acc, row, _) => {
        let line = {
          itemNumber: row.article.ArtNr,
          lineNumber: row.lineNumber,
          lineNumberPosition: row.lineNumberPosition,
          salesUnit: row.unit === 'p2' ? 'p' : row.unit,
          salesUnitQuantity: row.amount,
          // jointDeliveryCode: row.jointDeliveryCode, // Don't think this will ever be set here because we don't have a freightDelivery
        } as OrderLine;

        // if (!row._meta?.addon) line.lineNumberPosition = '' + (idx + 1);
        // if (freightDelivery) {
        //   let transports = freightDelivery.delivery.reduce((acc, delivery) => {
        //     acc.push(delivery.jointDeliveryCode);
        //     return acc;
        //   }, [] as string[]);

        //   for (let delivery of freightDelivery.delivery) {
        //     if (delivery.orderLines.find((orderLine) => orderLine.orderLineNumber === row.lineNumber)) {
        //       line.transportId = transports.indexOf(delivery.jointDeliveryCode) + 1;
        //     }
        //   }
        // }

        if (row.comment?.length) {
          line.preTexts = row.comment.split('\n').map((line, index) => ({
            lineNumber: index + 1,
            text: line,
            typeCode: row?.preDocumentClass ? row.preDocumentClass : 'CO02',
          }));
        }

        if (row.batchnr && !row._meta?.treatment) {
          line.batchNumber = row.batchnr;
        }

        acc.push(line);
        return acc;
      }, [] as OrderLine[]) ?? [];
  }

  return orderlines;
}

export function processProductlistRows(rows: any[]) {
  if (!rows?.length) return [];

  const blockedArticles = rows.reduce((col, row) => ({ [row.id]: articleBlocked1299(row.article), ...col }), {});

  const modifiedRows = rows;
  return modifiedRows.map((row) => {
    row.quantity = `${row.amount} ${convertUnit(row.salesUnit || 'st', { type: 'short', lowercase: true })}`;
    row.isBlocked = blockedArticles[row.id];

    if (row.override_prices) {
      row.price_manager = row.override_prices;
    }

    return row;
  });
}

export interface ArticleDetails {
  artnr: string;
  unit: string;
  quantity: number;
}
/**
 *
 * @param selectedArticles - List of articles to convert to a customer offer
 * @param options - Options for the customer offer
 * @param options.customerNumber - Customer number to create the offer for
 * @param options.user - User creating the offer
 * @param options.warehouse - Warehouse to use for the offer
 * @param options.address - Address to use for the offer
 *
 * @throws {Error} -
 */
export async function createQuoteFromArticles(
  selectedArticles: ArticleDetails[],
  {
    customerNumber,
    user,
    warehouse,
    address,
    terms,
    weight,
  }: {
    customerNumber: string;
    user: User;
    warehouse?: string;
    address?: Address;
    terms?: {
      deliveryType?: string;
      deliveryTerms?: string;
      packagingTerms?: string;
    };
    weight?: number;
  }
) {
  const customer = user.customers?.find((c) => c.customerNumber === customerNumber);
  if (!customer) {
    throw new Error('User does not have access to the provided customer number');
  }

  const contacts = await getAccountContacts().then((data) => data.data ?? []);
  const contact = contacts.find((c) => c.customerNumber === customerNumber);
  if (!contact) {
    // Note: Old implementation would just grab the first available contact if the customer number was not found.
    // But that seems like the wrong way to handle it?
    throw new Error('No contact found for the provided customer number');
  }

  const quote: Quote = {
    customerNumber: customer.customerNumber,
    customerStatus: customer.status,
    createdBy: [user?.user?.firstName, user?.user?.lastName].join(' '),
    currency: customer.currency,
    priceListTable: customer.priceListTable,
    mobilePhoneNumber: contact.mobileNo ?? '',
    directNumber: contact.telephoneNo,
    email: contact.email,
    status: '20',
    deliveryName: address?.companyName ?? customer.companyName,
    deliveryAddress1: address?.address1 ?? customer.address1,
    deliveryAddress2: address?.address2 ?? customer.address2,
    deliveryCity: address?.city ?? customer.city,
    deliveryPostalCode: address?.postalCode ?? customer.postalCode,
    deliveryType: terms?.deliveryType ?? '',
    deliveryTerms: terms?.deliveryTerms ?? '',
    packagingTerms: terms?.packagingTerms ?? '',
    warehouse: warehouse ?? customer.warehouse,
    grossWeight: weight ?? 0,
    offerIsEmpty: false,
    offerLines: [],
  };

  const { region } = useAccountStore();

  const packaging = new Packaging(import.meta.env.VITE_APP_PRODUCT_API_BASE_URL + '/orders/packaging', {
    region,
    warehouse: '12B', // TODO: Set proper warehouse
  });
  const packData = await packaging.fetch(
    selectedArticles.map((article, index) => {
      return {
        itemNumber: article.artnr,
        lineNumber: `${index * 10}`,
        lineNumberPosition: undefined,
        quantity: article.quantity,
        unit: article.unit,
      };
    })
  );

  // Fetch articles
  const fetchedArticles = await ArticleHelper.getAll({
    artnrs: [
      ...selectedArticles.map((article) => article.artnr),
      ...packData.orderPackagingResponsePackageLines.map((line) => line.itemNumber),
    ],
  });

  interface ArticleDetailsWithData extends ArticleDetails {
    article: Article;
    price: PriceManager;
    _meta?: any;
  }

  const combinedArticles: ArticleDetailsWithData[] = selectedArticles.map((article) => {
    const fetchedArticle = fetchedArticles[article.artnr];
    if (!fetchedArticle) {
      throw new Error(`Article ${article.artnr} not found`);
    }
    const unitAmount = fetchedArticle.unit?.[article.unit] ?? 1;
    return {
      ...article,
      article: fetchedArticle,
      price: new PriceManager({
        artnr: article.artnr,
        customer: customerNumber,
        address: address?.benders_address_id ?? '',
        warehouse: warehouse ?? customer.warehouse,
        region: user.region ?? '',
        quantity: article.quantity * unitAmount,
        unit: fetchedArticle.basicUnitOfMeasure,
      }),
    };
  });

  const packagingLines: ArticleDetailsWithData[] = packData.orderPackagingResponsePackageLines.map((line) => {
    const fetchedArticle = fetchedArticles[line.itemNumber];
    if (!fetchedArticle) {
      throw new Error(`Article ${line.itemNumber} not found`);
    }
    const unitAmount = fetchedArticle.unit?.[line.unit] ?? 1;
    return {
      artnr: line.itemNumber,
      quantity: line.quantity,
      unit: line.unit,
      article: fetchedArticle,
      price: new PriceManager({
        artnr: line.itemNumber,
        customer: customerNumber,
        address: address?.benders_address_id ?? '',
        warehouse: warehouse ?? customer.warehouse,
        region: user.region ?? '',
        quantity: line.quantity * unitAmount,
        unit: fetchedArticle.basicUnitOfMeasure,
      }),
      _meta: {
        addon: true,
        addon_type: 'pallet',
      },
    };
  });

  combinedArticles.push(...packagingLines);

  // Wait for prices to be fetched
  await Promise.all(combinedArticles.map((article) => article.price.awaitFetching()));

  console.log('combinedArticles', combinedArticles);

  let totalWeight = 0;
  quote.offerLines = combinedArticles.map((row, i) => {
    const price = {
      //netPrice: row.article.price?.netPrice,
      //salesPrice: row.article.price?.salesPrice,
      //linePrice: row.article.line_price?.lineAmount,
      netPrice: row.price.getPrice(PriceType.Net).value,
      salesPrice: row.price.getPrice(PriceType.Sales).value,
      linePrice: row.price.getPriceTotal(PriceType.Line).value,
    };
    const unitAmount = row.article.unit?.[row.unit] ?? 1;
    const baseunitAmount = getArticleBaseUnitAmount(row.article, { ...row, amount: row.quantity });

    let line = {
      description: recursiveGet(row.article, 'ItemDescription', 'SWEDISH', 0, 'value'),
      descriptionExt: getArticleTitle(row.article),
      inventoryAccounting: `${row.article.inventoryAccounting}`,
      itemNumber: row.article.ArtNr,
      quantity: row.quantity,
      unit: row.unit,
      salesUnit: row.unit,
      //salesUnitQuantity: row.baseunitAmount.value,
      salesUnitQuantity: baseunitAmount.value,
      netPrice: price.netPrice ? price.netPrice * row.quantity * unitAmount : undefined,
      grossPrice: price.salesPrice ? price.salesPrice * row.quantity * unitAmount : undefined,
      basicUnitOfMeasure: row.article.basicUnitOfMeasure,
      salesUnitQuantityMap: row.article.unit,
      discount: 0,
      tg: 0,
      tb: 0,
    } as {
      description: string;
      descriptionExt: string;
      inventoryAccounting: string;
      itemNumber: string;
      quantity: number;
      unit: string;
      salesUnit: string;
      salesUnitQuantity: number;
      netPrice?: number;
      grossPrice?: number;
      basicUnitOfMeasure: string;
      salesUnitQuantityMap: any;
      discount: number;
      tg: number;
      tb: number;
      isExtraRow?: boolean;
      index?: number;
    };

    totalWeight += row.article.weight.gross * line.salesUnitQuantity;

    if (row._meta?.addon) {
      line.description = getArticleTitle(row.article);
      line.isExtraRow = row._meta.addon_type;
      line.index = i;
      line.salesUnitQuantity = 1; // Has to be 1 for addons, otherwise the perItem cost is wrong

      /*if (row._meta.addon_type === 'delivery') {
        line.netPrice = row.article.price.price_total;
        line.grossPrice = row.article.price.price_total;
      }*/
    }

    // Calculate TG/TB
    const gross = +(line.grossPrice ?? 0);
    let tg = 0;
    let tb = 0;
    if (gross) {
      tb = gross - (price?.linePrice ?? 0);
      tg = (tb / gross) * 100;
      tg = isFinite(tg) ? tg : 0;
      tb = isFinite(tb) ? tb : 0;
      tg = +(('' + tg).match(/(^-?\d*(?:[.,]\d{0,2})?)/)?.[0] ?? 0); // To 2 decimal places
      tb = +(('' + tb).match(/(^-?\d*(?:[.,]\d{0,2})?)/)?.[0] ?? 0); // To 2 decimal places
    }

    line.tg = tg;
    line.tb = tb;

    return line;
  }, []);

  if (!weight) {
    quote.grossWeight = totalWeight;
  }

  return saveQuote(undefined, quote);
}

export function buildTransportTexts(data: {
  deliverySpecification?: string;
  packagingTerms: string;
  deliveryDate: dayjs.ConfigType;
  existingLines?: OrderText[];
  extraLines?: string[];
}): OrderText[] | undefined {
  const texts = [] as OrderText[];
  const appendText = (text?: string, typeCode?: string) => {
    if (!text && !typeCode) {
      throw new Error('Text and typeCode cannot both be undefined');
    }
    // Check if text already exists, to avoid duplicates
    if (texts.some((t) => t.text === text && t.typeCode === typeCode)) {
      return;
    }

    texts.push({ lineNumber: texts.length + 1, text, typeCode });
  };

  data.existingLines?.forEach((line) => appendText(line.text, line.typeCode));

  if (data.deliverySpecification) {
    appendText(data.deliverySpecification);
  }

  if (data.packagingTerms === 'ÖNS' && data.deliveryDate) {
    appendText(
      i18n.global.t('order.transport_texts.desired_delivery_date', {
        date: dayjs(data.deliveryDate).format(i18n.global.t('pikaday.date_format')),
      })
    );
  }

  data.extraLines?.forEach((line) => appendText(line));

  return texts.length > 0 ? texts : undefined;
}
